// MCP Security — event detail side drawer.
//
// Rebuilt 2026-10-12 to match the WAF Smart Parse / Signature Event
// drawer family. Reuses the shared primitives:
//   - WafEventDrawerShell (hero header variant, 720 px, close affordance)
//   - SmartParseDrawerHeader (same mark + title + method/path row)
//   - SmartParseSectionCard (section headers, collapse chevron)
//   - EventAssessGrid / AssessGridCell (key-value grid)
//   - SmartParseRawJsonViewer (raw event viewer)
//   - DrawerFooterContext-style footer composition
// so the two drawers read as one surface.
//
// MCP-only sections preserved:
//   - Reason banner (plain-English explanation of each outcome)
//   - Decision timeline (reads details.pipeline[] when present, else
//     derives a per-stage share from latency_ms)
//   - Session chain (GET /mcp/sites/:site_uid/sessions/:fp/timeline)
//   - Actions (allow-tool, mark-FP, open-in-events, reprobe)
//
// Field map for the current CH row:
//   ts, request_id, site_uid, endpoint_id, tool_name, state, action,
//   outcome, http_status, latency_ms, client_ip, user_agent, session_fp,
//   details, host.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Stack, Typography, Chip, Button,
} from "@mui/material";
import copy from "copy-to-clipboard";
import {
  Clock, Globe, Shield, Target, Workflow, GitBranch, Activity,
  ScanSearch, Hash, AlertTriangle, CheckCircle2, Info, User, Fingerprint,
} from "lucide-react";

import axios from "../../../../../utils/axios/v1/userAxios";
import colors from "../../../../../utils/colors";
import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import { formatDate } from "../../../../../utils/format";
import { useNotification } from "../../../../../contexts/NotificationContext";

import WafEventDrawerShell from "../../detection/WafEventDrawerShell";
import SmartParseDrawerHeader from "../../detection/v3/SmartParseDrawerHeader";
import SmartParseRawJsonViewer from "../../detection/v3/SmartParseRawJsonViewer";
import {
  EventAssessGrid,
  SmartParseSectionCard,
  spSecondaryBodySx,
  spLh,
} from "../../detection/v3/smartParseDrawerUi";
import {
  PRIMARY_COLOR, TEXT_PRIMARY, TEXT_SECONDARY, BORDER_COLOR,
  secondaryButtonStyle, primaryButtonStyle,
} from "../../detection/wafEventDrawerShared";

import { outcomePalette } from "./_shared/OutcomePill";

// ─────────────────────────────────────────────────────────────────────
// Outcome chip — same shape as the WAF status chip (height 24, radius
// 6px, px 10px, fontSize 12, fontWeight 600) but with the MCP outcome
// palette.
// ─────────────────────────────────────────────────────────────────────
const OutcomeChip = ({ outcome, size = "md" }) => {
  const p = outcomePalette(outcome);
  const compact = size === "sm";
  return (
    <Chip
      size="small"
      label={p.label || outcome || "—"}
      sx={{
        height: compact ? 22 : 24,
        fontSize: compact ? 11 : 12,
        fontWeight: 600,
        borderRadius: "6px",
        backgroundColor: p.bg,
        color: p.fg,
        border: `1px solid ${p.border}`,
        "& .MuiChip-label": { px: "10px", py: 0, lineHeight: 1.2 },
      }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────
// Plain-English explanation of each outcome slug (unchanged logic from
// the pre-rebuild drawer).
// ─────────────────────────────────────────────────────────────────────
function explainOutcome(event, parsedDetails) {
  const o = String(event?.outcome || "").toLowerCase();
  const hits = parsedDetails || {};
  const list = (arr) => (Array.isArray(arr) && arr.length ? arr.join(", ") : null);

  switch (o) {
    case "pass":
    case "suppress":
      return {
        kind: "pass",
        title: "Passed through",
        body: "This request matched a configured MCP endpoint and was forwarded to the upstream MCP server. The generic WAF / API-catalog / signatures stack stepped aside so JSON-RPC traffic didn't false-positive against them.",
      };
    case "tag":
      return {
        kind: "info",
        title: "Tagged — modules ran",
        body: "The endpoint is in log-only mode or stale state, so pingora recorded the MCP match but let the generic WAF / rate-limit / signatures modules evaluate the request normally. No MCP-aware policy was applied.",
      };
    case "block_new_tool":
    case "drift_block":
      return {
        kind: "block",
        title: "Blocked — tool-manifest drift",
        body: "The endpoint is under drift_hold because its tool manifest changed since the baselined probe. Requests calling tools the operator hasn't approved yet get a 403. Known / already-approved tools still pass. The pingora data plane returns JSON-RPC error -32011 with CES mcp.drift_block.",
        trigger: {
          label: "Called tool not in approved manifest",
          value: event?.tool_name || "(no tool_name resolved; drift_hold blocks unknown tools too)",
        },
      };
    case "scope_denied":
      return {
        kind: "block",
        title: "Blocked — insufficient OAuth scope",
        body: "The called tool declares a required scope list in its registry entry; the caller's bearer-token scopes don't cover every required scope. Pingora returns 403 with JSON-RPC error -32016 and CES mcp.scope_denied before the request reaches the MCP server.",
        trigger: {
          label: "Missing scopes",
          value: list(hits.scope_required) || "(the required-scopes list isn't captured in details for this build)",
        },
      };
    case "session_integrity":
      return {
        kind: "block",
        title: "Blocked — session fingerprint mismatch",
        body: "The Mcp-Session-Id presented came with a different client fingerprint (sha256(client_ip | user_agent)) than the one that first established the session. The endpoint is in session_mode=enforce so pingora returned 401 with JSON-RPC error -32015 and CES mcp.session_integrity. Possible session-hijack signature.",
        trigger: {
          label: "Expected vs seen fingerprint",
          value: event?.session_fp
            ? `This request's fp: ${event.session_fp.slice(0, 16)}…`
            : "(not captured)",
        },
      };
    case "session_cap":
      return {
        kind: "block",
        title: "Blocked — per-session request cap exceeded",
        body: "This session_id went over the per-minute cap configured on the endpoint. Pingora returned 429 with JSON-RPC error -32017 and CES mcp.session_cap. The cap is tracked via INCR+EXPIRE in Redis per {endpoint_id:session_id}:<minute-bucket>.",
      };
    case "rate_limit":
      return {
        kind: "block",
        title: "Blocked — per-tool rate budget exceeded",
        body: `The tool "${event?.tool_name || "?"}" is over its per-minute budget on this endpoint. Pingora returned 429 with JSON-RPC error -32005 and CES mcp.rate_limit. Budget is enforced via INCR+EXPIRE in Redis per {endpoint_id:tool_name}:<minute-bucket>.`,
      };
    case "prompt_injection_blocked":
      return {
        kind: "block",
        title: "Blocked — prompt-injection in tool response",
        body: "The upstream MCP server's response body carried signatures the scanner recognises (Llama / ChatML control tokens, override phrases, zero-width unicode, role-switch sentinels). The endpoint is in injection_mode=block so pingora replaced the response body with a JSON-RPC error envelope carrying CES mcp.prompt_injection_blocked.",
        trigger: {
          label: "Signatures matched",
          value: list(hits.injection) || "(hit list not captured in this row's details)",
        },
      };
    case "response_too_large":
      return {
        kind: "block",
        title: "Truncated — response size cap exceeded",
        body: "The upstream MCP server returned a body larger than the per-endpoint response_cap_bytes. Pingora truncated the stream at the cap and spliced in a JSON-RPC error envelope with CES mcp.response_too_large. Default cap is 1 MiB.",
      };
    case "credential_leak":
      return {
        kind: "warn",
        title: "Flagged — credential-shape in response",
        body: "The upstream tool response contained strings matching the credential-shape scanner (AWS AKIA/ASIA, OpenAI sk-, Anthropic sk-ant-, GitHub ghp_ / github_pat_, Google AIzaSy, Slack xoxb-, Stripe sk_live_, or a bearer token ≥32 chars). Depending on credential_mode this was either logged (log_only) or overwritten with `*` glyphs (redact).",
        trigger: {
          label: "Patterns matched",
          value: list(hits.credential) || "(pattern list not captured in this row's details)",
        },
      };
    default:
      return {
        kind: "info",
        title: `Outcome: ${event?.outcome || "unknown"}`,
        body: "No plain-English mapping registered for this outcome slug yet. See the Scanner hits + Raw event sections below for more context.",
      };
  }
}

function prettyUserAgent(ua) {
  if (!ua) return "";
  const matches = {
    firefox:     /Firefox\/(\S+)/,
    chrome:      /Chrome\/(\S+)/,
    safari:      /Version\/(\S+).*Safari/,
    curl:        /^curl\/(\S+)/,
    python:      /python-requests\/(\S+)/,
    node:        /node[/-](\S+)/i,
    go:          /^Go-http-client\/(\S+)/,
    layerdprobe: /^layerd-mcp-prober/,
  };
  for (const [name, re] of Object.entries(matches)) {
    const m = ua.match(re);
    if (m) return `${name}${m[1] ? ` ${m[1]}` : ""}`;
  }
  return ua.length > 60 ? ua.slice(0, 60) + "…" : ua;
}

// Reads details.pipeline[] when the pingora writer provided wall-clock
// stage data; otherwise derives a fixed share-of-latency.
function buildStages(event, parsedDetails) {
  if (parsedDetails?.pipeline && Array.isArray(parsedDetails.pipeline) && parsedDetails.pipeline.length > 0) {
    const total = Math.max(1, parsedDetails.pipeline.reduce((a, s) => a + (s.ms || 0), 0));
    let acc = 0;
    const base = outcomePalette(event?.outcome).fg;
    return parsedDetails.pipeline.map((s) => {
      const w = (s.ms || 0) / total;
      const row = {
        name: s.name || s.stage || "stage",
        ms: s.ms || 0,
        x: `${(acc * 100).toFixed(1)}%`,
        w: `${(w * 100).toFixed(1)}%`,
        barColor: base,
      };
      acc += w;
      return row;
    });
  }
  const total = Math.max(1, event?.latency_ms || 0);
  const share = {
    route: 0.03, read_body: 0.08, parse: 0.07,
    scanners: 0.55, upstream: 0.22, respond: 0.05,
  };
  const base = outcomePalette(event?.outcome).fg;
  const order = ["route", "read_body", "parse", "scanners", "upstream", "respond"];
  let acc = 0;
  return order.map((name) => {
    const w = share[name];
    const row = {
      name,
      ms: Math.round(total * w),
      x: `${(acc * 100).toFixed(1)}%`,
      w: `${(w * 100).toFixed(1)}%`,
      barColor: base,
    };
    acc += w;
    return row;
  });
}

function anchorRow(event) {
  if (!event) return [];
  return [{
    n: "0",
    time: event.ts ? new Date(event.ts).toLocaleTimeString() : "—",
    tool: event.tool_name || "—",
    outcome: event.outcome,
    bg: "rgba(99,113,247,0.06)",
    current: true,
  }];
}

export default function McpEventDrawer({ open, event, siteUid, onClose }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [busyAction, setBusyAction] = useState(null); // "approve" | "fp" | "reprobe"

  const parsedDetails = useMemo(() => {
    if (!event?.details) return null;
    if (typeof event.details === "object") return event.details;
    try { return JSON.parse(event.details); } catch (_) { return null; }
  }, [event]);

  const approveTool = async () => {
    if (!event?.endpoint_id || !event?.tool_name || busyAction) return;
    setBusyAction("approve");
    try {
      await axios.post(
        `mcp/endpoints/${event.endpoint_id}/tools/${encodeURIComponent(event.tool_name)}/approve`,
      );
      showNotification({ severity: "success", message: `${event.tool_name}: allowed` });
    } catch (e) {
      showNotification({
        severity: "error",
        message: e?.response?.data?.message || e.message || "approve failed",
      });
    } finally {
      setBusyAction(null);
    }
  };

  const markFalsePositive = async () => {
    const eventId = event?.request_id || event?.event_id;
    if (!siteUid || !eventId || busyAction) return;
    setBusyAction("fp");
    try {
      await axios.post(
        `mcp/sites/${siteUid}/events/${encodeURIComponent(eventId)}/mark_fp`,
        {},
      );
      showNotification({ severity: "success", message: "Marked as false positive" });
    } catch (e) {
      showNotification({
        severity: "error",
        message: e?.response?.data?.message || e.message || "mark_fp failed",
      });
    } finally {
      setBusyAction(null);
    }
  };

  const reprobeFromDrawer = async () => {
    if (!siteUid || !event?.endpoint_id || busyAction) return;
    setBusyAction("reprobe");
    try {
      await axios.post(
        `mcp/sites/${siteUid}/endpoints/${event.endpoint_id}/reprobe`,
      );
      showNotification({ severity: "info", message: "Reprobe enqueued" });
    } catch (e) {
      showNotification({
        severity: "error",
        message: e?.response?.data?.message || e.message || "reprobe failed",
      });
    } finally {
      setBusyAction(null);
    }
  };

  const stages = useMemo(() => (event ? buildStages(event, parsedDetails) : []), [event, parsedDetails]);

  const [chain, setChain] = useState([]);
  const [chainErr, setChainErr] = useState(null);
  useEffect(() => {
    if (!event || !event.session_fp || !siteUid) {
      setChain(anchorRow(event));
      return;
    }
    let cancelled = false;
    axios.get(`mcp/sites/${siteUid}/sessions/${event.session_fp}/timeline?limit=10`)
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.data || [];
        const rows = items.map((t, i) => ({
          n: String(items.length - i),
          time: t.ts ? new Date(t.ts).toLocaleTimeString() : (t.time || "—"),
          tool: t.tool || t.tool_name || "—",
          outcome: t.outcome || "pass",
          bg: "#fff",
        }));
        if (event.ts) {
          rows.push({
            n: "·",
            time: new Date(event.ts).toLocaleTimeString(),
            tool: event.tool_name || "—",
            outcome: event.outcome,
            bg: "rgba(99,113,247,0.06)",
            current: true,
          });
        }
        setChain(rows);
        setChainErr(null);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setChain(anchorRow(event));
          setChainErr(null);
        } else {
          setChainErr(e?.response?.data?.message || e.message);
          setChain(anchorRow(event));
        }
      });
    return () => { cancelled = true; };
  }, [event, siteUid]);

  const handleCopy = (value) => {
    if (!value || value === "—") return;
    copy(String(value));
  };

  if (!event) return null;

  const reason = explainOutcome(event, parsedDetails);
  const reasonPalette = {
    pass:  { bg: "#F0F9F4", bd: "#A5D5B9", fg: "#1E6B3F", icon: CheckCircle2 },
    info:  { bg: "#EEF4FD", bd: "#B9CCF2", fg: "#1A4FBF", icon: Info },
    warn:  { bg: "#FFF8E6", bd: "#F3DC9A", fg: "#8A6410", icon: AlertTriangle },
    block: { bg: "#FBECEC", bd: "#E8B8B8", fg: "#8E1F1F", icon: AlertTriangle },
  }[reason.kind];
  const ReasonIcon = reasonPalette.icon;

  // Assessment grid — mirrors the WAF Signature drawer's Assessment card.
  const assessCells = [
    {
      key: "timestamp",
      label: "Timestamp",
      value: event.ts ? formatDate(event.ts) : null,
      icon: Clock,
    },
    {
      key: "hostname",
      label: "Hostname",
      value: event.host,
      icon: Globe,
      valueColor: PRIMARY_COLOR,
    },
    {
      key: "endpoint",
      label: "Endpoint id",
      value: event.endpoint_id,
      icon: Target,
      monospace: true,
      copyable: true,
    },
    {
      key: "tool",
      label: "Tool",
      value: event.tool_name,
      icon: DETECTION_ICONS.wrench,
      monospace: true,
    },
    {
      key: "action",
      label: "Action",
      value: event.action,
      icon: Shield,
    },
    {
      key: "state",
      label: "State",
      value: event.state,
      icon: Activity,
    },
    {
      key: "http",
      label: "HTTP",
      value: event.http_status != null ? String(event.http_status) : null,
      icon: DETECTION_ICONS.responseStatus,
      monospace: true,
      valueColor: Number(event.http_status) >= 400 ? "#B91C1C" : TEXT_PRIMARY,
    },
    {
      key: "latency",
      label: "Latency",
      value: event.latency_ms != null ? `${event.latency_ms} ms` : null,
      icon: Activity,
      monospace: true,
    },
    event.agent_id
      ? { key: "agent", label: "Agent", value: event.agent_id, icon: User }
      : null,
    event.model_id
      ? { key: "model", label: "Model", value: event.model_id, icon: DETECTION_ICONS.ml, monospace: true }
      : null,
  ].filter(Boolean);

  const clientCells = [
    {
      key: "client_ip",
      label: "Client IP",
      value: event.client_ip,
      icon: DETECTION_ICONS.ip,
      monospace: true,
      copyable: true,
    },
    {
      key: "country",
      label: "Country",
      value: event.country || null,
      icon: Globe,
    },
    {
      key: "ua",
      label: "User-Agent",
      value: prettyUserAgent(event.user_agent),
      icon: DETECTION_ICONS.header,
      monospace: true,
    },
    {
      key: "session",
      label: "Session fp",
      value: event.session_fp,
      icon: Fingerprint,
      monospace: true,
      copyable: true,
    },
    {
      key: "request",
      label: "Request id",
      value: event.request_id,
      icon: Hash,
      monospace: true,
      copyable: true,
    },
    event.tokens_in || event.tokens_out
      ? {
        key: "tokens",
        label: "Tokens (in/out)",
        value: `${event.tokens_in ?? "—"} / ${event.tokens_out ?? "—"}`,
        icon: DETECTION_ICONS.ml,
        monospace: true,
      }
      : null,
  ].filter(Boolean);

  // ── Footer composition (matches Signature WAF drawer) ────────────
  const footerLeft = event.host ? (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: colors.green[200],
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: 13,
          lineHeight: spLh.px21,
          color: TEXT_SECONDARY,
          wordBreak: "break-all",
        }}
      >
        {event.host}
      </Typography>
    </Stack>
  ) : null;

  const footerActions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Button
        onClick={onClose}
        startIcon={<LayerdIcon icon={DETECTION_ICONS.close} size={16} color="neutral" />}
        sx={secondaryButtonStyle}
      >
        Close
      </Button>
      <Button
        onClick={approveTool}
        disabled={!event?.tool_name || !event?.endpoint_id || busyAction === "approve"}
        sx={{
          ...primaryButtonStyle,
          boxShadow: "0 4px 14px rgba(99,113,247,0.30)",
        }}
      >
        {busyAction === "approve" ? "Approving…" : "Add tool to allow-list"}
      </Button>
    </Stack>
  );

  const header = (
    <SmartParseDrawerHeader
      embedded
      title="MCP Event"
      eventId={event.request_id}
      method={event.outcome}
      path={event.tool_name || event.endpoint_id || event.host}
      onCopyEventId={handleCopy}
    />
  );

  // Hero content — reason banner + top-level pills, rendered in the
  // same hero slot the Signature WAF drawer uses.
  const hero = (
    <Stack spacing={1.5} sx={{ pt: 1 }}>
      <Box
        sx={{
          border: `1px solid ${reasonPalette.bd}`,
          borderRadius: "12px",
          bgcolor: reasonPalette.bg,
          p: 2,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box sx={{ pt: 0.25 }}>
            <ReasonIcon size={18} color={reasonPalette.fg} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: reasonPalette.fg, mb: 0.5 }}>
              {reason.title}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: TEXT_PRIMARY, lineHeight: 1.55 }}>
              {reason.body}
            </Typography>
            {reason.trigger && (
              <Box
                sx={{
                  mt: 1, pt: 1,
                  borderTop: `1px dashed ${reasonPalette.bd}`,
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "baseline",
                }}
              >
                <Typography sx={{ fontSize: 11, color: reasonPalette.fg, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                  {reason.trigger.label}:
                </Typography>
                <Typography sx={{ fontSize: 12, color: TEXT_PRIMARY, fontFamily: "monospace" }}>
                  {reason.trigger.value}
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <OutcomeChip outcome={event.outcome} />
        <Chip size="small" variant="outlined" label={`action: ${event.action || "—"}`}
          sx={{ height: 24, fontSize: 11.5, borderRadius: "6px", borderColor: BORDER_COLOR }} />
        <Chip size="small" variant="outlined" label={`state: ${event.state || "—"}`}
          sx={{ height: 24, fontSize: 11.5, borderRadius: "6px", borderColor: BORDER_COLOR }} />
        <Chip size="small" variant="outlined" label={`HTTP ${event.http_status ?? "—"}`}
          sx={{ height: 24, fontSize: 11.5, borderRadius: "6px", borderColor: BORDER_COLOR, fontFamily: "monospace" }} />
        <Chip size="small" variant="outlined" label={`${event.latency_ms ?? 0} ms`}
          sx={{ height: 24, fontSize: 11.5, borderRadius: "6px", borderColor: BORDER_COLOR, fontFamily: "monospace" }} />
      </Stack>
    </Stack>
  );

  return (
    <WafEventDrawerShell
      open={open}
      onClose={onClose}
      title="MCP Event"
      headerVariant="hero"
      headerContent={header}
      heroContent={hero}
      footerLeft={footerLeft}
      footerActions={footerActions}
    >
      <SmartParseSectionCard icon={DETECTION_ICONS.clipboard} title="Assessment">
        <EventAssessGrid cells={assessCells} onCopy={handleCopy} />
      </SmartParseSectionCard>

      <SmartParseSectionCard icon={Workflow} title="Decision timeline">
        <Box sx={{ border: `1px solid ${BORDER_COLOR}`, borderRadius: 2, overflow: "hidden" }}>
          {stages.map((st) => (
            <Box
              key={st.name}
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 160px 54px",
                gap: 1,
                alignItems: "center",
                px: 1.5,
                py: 0.75,
                borderBottom: `1px solid ${BORDER_COLOR}`,
                fontSize: 12,
                "&:last-child": { borderBottom: "none" },
              }}
            >
              <Typography sx={{ fontSize: 12, color: TEXT_PRIMARY, fontFamily: "monospace" }}>{st.name}</Typography>
              <Box sx={{ height: 6, background: "#F3F4F6", borderRadius: 999, position: "relative" }}>
                <Box sx={{
                  position: "absolute", top: 0, bottom: 0,
                  borderRadius: 999,
                  left: st.x, width: st.w,
                  background: st.barColor,
                }} />
              </Box>
              <Typography sx={{ fontFamily: "monospace", textAlign: "right", color: TEXT_SECONDARY, fontSize: 12 }}>
                {st.ms}ms
              </Typography>
            </Box>
          ))}
        </Box>
      </SmartParseSectionCard>

      <SmartParseSectionCard icon={DETECTION_ICONS.ip} title="Client">
        <EventAssessGrid cells={clientCells} onCopy={handleCopy} />
      </SmartParseSectionCard>

      {parsedDetails && Object.keys(parsedDetails).length > 0 && (
        <SmartParseSectionCard icon={ScanSearch} title="Scanner hits">
          <Stack spacing={1.5}>
            {Object.entries(parsedDetails).map(([category, hits]) => {
              if (category === "pipeline") return null;
              return (
                <Box key={category}>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 700, color: "#8A8FA8",
                    textTransform: "uppercase", letterSpacing: "0.1em", mb: 0.5,
                  }}>
                    {category.replace(/_/g, " ")}
                  </Typography>
                  {Array.isArray(hits) && hits.length ? (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {hits.map((h, i) => (
                        <Chip
                          key={i}
                          size="small"
                          label={typeof h === "string" ? h : JSON.stringify(h)}
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 500,
                            borderRadius: "999px",
                            backgroundColor: colors.neutral[50],
                            color: colors.neutral[600],
                            border: `1px solid ${BORDER_COLOR}`,
                            fontFamily: "monospace",
                            "& .MuiChip-label": { px: "10px", py: 0, lineHeight: 1.2 },
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ ...spSecondaryBodySx, fontFamily: "monospace", color: TEXT_PRIMARY }}>
                      {String(hits)}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </SmartParseSectionCard>
      )}

      {event.session_fp && (
        <SmartParseSectionCard
          icon={GitBranch}
          title={`Session chain · ${event.session_fp.slice(0, 10)}`}
          headerExtra={(
            <Typography
              onClick={() => navigate(`/v2/mcp/sessions`)}
              sx={{
                fontSize: 12, color: PRIMARY_COLOR, cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View session
            </Typography>
          )}
        >
          {chainErr && (
            <Typography sx={{ fontSize: 11, color: "#8E1F1F", mb: 1 }}>
              Timeline unavailable: {chainErr}
            </Typography>
          )}
          <Box sx={{ border: `1px solid ${BORDER_COLOR}`, borderRadius: 2, overflow: "hidden" }}>
            {chain.map((c, i) => (
              <Stack
                key={i}
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{
                  px: 1.5, py: 0.75,
                  borderBottom: i < chain.length - 1 ? `1px solid ${BORDER_COLOR}` : "none",
                  background: c.bg || "#fff",
                  fontSize: 12,
                }}
              >
                <Typography sx={{ fontFamily: "monospace", color: TEXT_SECONDARY, width: 22, fontSize: 12 }}>{c.n}</Typography>
                <Typography sx={{ fontFamily: "monospace", color: TEXT_SECONDARY, width: 64, fontSize: 12 }}>{c.time}</Typography>
                <Typography sx={{ fontFamily: "monospace", flex: 1, fontSize: 12 }}>{c.tool}</Typography>
                <OutcomeChip outcome={c.outcome} size="sm" />
              </Stack>
            ))}
          </Box>
        </SmartParseSectionCard>
      )}

      <SmartParseSectionCard icon={Activity} title="Actions">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            size="small"
            sx={{
              ...primaryButtonStyle,
              height: 32,
              fontSize: 13,
            }}
            onClick={approveTool}
            disabled={!event?.tool_name || !event?.endpoint_id || busyAction === "approve"}
          >
            {busyAction === "approve" ? "Approving…" : "Add tool to allow-list"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ ...secondaryButtonStyle, height: 32, fontSize: 13 }}
            onClick={markFalsePositive}
            disabled={!(event?.request_id || event?.event_id) || !siteUid || busyAction === "fp"}
          >
            {busyAction === "fp" ? "Marking…" : "Mark as false positive"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ ...secondaryButtonStyle, height: 32, fontSize: 13 }}
            onClick={() => {
              onClose && onClose();
              navigate(`/v2/mcp/events?endpoint_id=${encodeURIComponent(event.endpoint_id)}`);
            }}
          >
            Open in Events
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ ...secondaryButtonStyle, height: 32, fontSize: 13 }}
            onClick={reprobeFromDrawer}
            disabled={!event?.endpoint_id || !siteUid || busyAction === "reprobe"}
          >
            {busyAction === "reprobe" ? "Enqueuing…" : "Reprobe endpoint"}
          </Button>
        </Stack>
      </SmartParseSectionCard>

      <SmartParseSectionCard icon={DETECTION_ICONS.info} title="Raw event" defaultExpanded={false}>
        <SmartParseRawJsonViewer
          data={event}
          onCopy={handleCopy}
          filename="mcp_event.json"
        />
      </SmartParseSectionCard>
    </WafEventDrawerShell>
  );
}
